import { ECrontabSpecialChars } from '@/components/Schedules/types';
import { getScheduleHourOnAppDay } from '@/utils/date';
import { addAppDays, appWallClockParts, floorToMinute, getAppDayWeekday } from '@/utils/spain-time';

import { TProgramSchedule } from '../types';

/**
 * One emission the generation would create: the slot it comes from and the
 * instants that slot's hours produce on its day.
 *
 * Days are Madrid calendar days and slot hours are Madrid wall clock, for every
 * viewer (RCOMM-6264): a `10:00` slot on Mondays is Monday 10:00 in Spain.
 */
export type TProgramBroadcastCandidate = {
  slot: TProgramSchedule;
  startDatetime: Date;
  endDatetime: Date;
  deliveryDatetime: Date | null;
};

type TGenerateCandidatesParams = {
  slots: TProgramSchedule[];
  startDate: Date;
  endDate: Date;
  limit?: number;
};

/**
 * What the walk produced, beyond the emissions themselves: the slots it
 * dropped for landing on an hour another slot already took, and whether it
 * stopped before the end of the period.
 */
export type TProgramBroadcastCandidatesReport = {
  candidates: TProgramBroadcastCandidate[];
  duplicatesDropped: number;
  isTruncated: boolean;
};

const ONE_DAY = 1;

/**
 * The most candidates one walk will materialise. A period of years against a
 * daily slot describes thousands of emissions, none of which can be generated
 * — the batch is capped far below this — and building them all costs a long
 * freeze before anything says so. The walk stops here and reports it, and the
 * caller turns that into a reason instead of a request.
 *
 * Set to the skip lookup's own page size: past this the lookup could not tell
 * the modal what already exists anyway.
 */
export const MAX_GENERATED_CANDIDATES = 1000;

/**
 * A schedule hour usable as `HH:mm` or `HH:mm:ss`. The hours come from the
 * programme's own form, but nothing guarantees what is stored: a value
 * `getScheduleHourOnAppDay` cannot read yields an Invalid Date, which would be
 * counted in the preview, rejected by the server and then break the very list
 * naming the failure.
 */
function isUsableHour(hour: string | undefined | null): boolean {
  const parts = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(hour?.trim() ?? '');

  if (!parts) return false;

  // The seconds group is optional, and an hour without it is a valid `HH:mm`.
  const [hours, minutes, seconds = '0'] = parts.slice(1);

  return Number(hours) <= 23 && Number(minutes) <= 59 && Number(seconds) <= 59;
}

/**
 * The Madrid calendar day (`yyyy-MM-dd`) an instant falls on, so days are
 * compared and walked as the calendar days of Spain rather than as instants or
 * as the viewer's own days.
 */
function toCalendarDay(date: Date): string {
  return appWallClockParts(date).date;
}

/**
 * A slot applies to a Madrid day (`yyyy-MM-dd`) when its crontab
 * `repetition_day_of_week` lists that day's weekday (Sunday `0` through
 * Saturday `6`), and to every day when it is empty or the crontab any-value `*`.
 */
export function doesSlotApplyToDay(slot: TProgramSchedule, day: string): boolean {
  const repetition = slot.repetition_day_of_week?.trim();

  if (!repetition || repetition === ECrontabSpecialChars.AnyValue) return true;

  return repetition
    .split(ECrontabSpecialChars.ValueListSeparator)
    .map(dayOfWeek => dayOfWeek.trim())
    .includes(getAppDayWeekday(day).toString());
}

/**
 * Builds the emission a slot produces on a day. The end and the delivery
 * instants are rolled to the following day when the slot's hours put them at or
 * before its start, so a slot crossing midnight ends after it starts and a
 * late-night slot delivering in the small hours is not rejected server-side.
 *
 * A slot whose hours cannot describe an emission — missing, or stored in a
 * shape `getScheduleHourOnAppDay` cannot read — produces none rather than an
 * emission with an invented boundary or an Invalid Date. `findSlotsMissingHours`
 * applies the same reading, so every slot dropped here is named to the user
 * instead of vanishing from the count.
 */
function buildCandidate(slot: TProgramSchedule, day: string): TProgramBroadcastCandidate | null {
  if (!isUsableHour(slot.start_hour) || !isUsableHour(slot.end_hour)) return null;
  if (slot.delivery_hour && !isUsableHour(slot.delivery_hour)) return null;

  const nextDay = addAppDays(day, ONE_DAY);
  const startDatetime = getScheduleHourOnAppDay(day, slot.start_hour);
  let endDatetime = getScheduleHourOnAppDay(day, slot.end_hour);

  // `<=`, not `<`: a slot whose two hours are the same describes a full day,
  // never an emission of zero length, which the server rejects and which would
  // reach the user as a failure rather than as the schedule it came from.
  if (endDatetime <= startDatetime) {
    endDatetime = getScheduleHourOnAppDay(nextDay, slot.end_hour);
  }

  let deliveryDatetime: Date | null = null;

  if (slot.delivery_hour) {
    deliveryDatetime = getScheduleHourOnAppDay(day, slot.delivery_hour);

    if (deliveryDatetime < startDatetime) {
      deliveryDatetime = getScheduleHourOnAppDay(nextDay, slot.delivery_hour);
    }
  }

  return { slot, startDatetime, endDatetime, deliveryDatetime };
}

/**
 * Walks every calendar day of the period against every slot and returns one
 * candidate per applying pair. Both bounds of the period are inclusive and the
 * candidates come back in day order, and within a day in the slots' own order.
 *
 * Two slots can land on the same start hour of the same day — an unrestricted
 * slot and a weekday one sharing an hour, which the modal's editable list makes
 * easy to produce. Only the first survives: that start, to the minute, is what
 * the skip rule treats as identity, so emitting both would create an emission that
 * the very next generation would recognise as a duplicate of itself. The report
 * counts the dropped ones, so the modal can say why the total is lower than the
 * slots suggest instead of losing them silently.
 *
 * The walk stops at `limit` candidates and says so, rather than materialising
 * a period of years nothing can generate anyway.
 *
 * Pure: it issues no request and reads nothing but its arguments.
 */
export function generateProgramBroadcastCandidates({
  slots,
  startDate,
  endDate,
  limit = MAX_GENERATED_CANDIDATES,
}: TGenerateCandidatesParams): TProgramBroadcastCandidatesReport {
  const lastDay = toCalendarDay(endDate);
  const candidates: TProgramBroadcastCandidate[] = [];
  const takenStarts = new Set<number>();
  let duplicatesDropped = 0;
  let isTruncated = false;

  // `yyyy-MM-dd` days compare in calendar order as strings.
  for (let day = toCalendarDay(startDate); day <= lastDay; day = addAppDays(day, ONE_DAY)) {
    if (isTruncated) break;

    slots.forEach(slot => {
      if (isTruncated) return;
      if (!doesSlotApplyToDay(slot, day)) return;

      const candidate = buildCandidate(slot, day);

      if (!candidate) return;

      const startStamp = floorToMinute(candidate.startDatetime).getTime();

      if (takenStarts.has(startStamp)) {
        duplicatesDropped += 1;

        return;
      }

      if (candidates.length >= limit) {
        isTruncated = true;

        return;
      }

      takenStarts.add(startStamp);
      candidates.push(candidate);
    });
  }

  return { candidates, duplicatesDropped, isTruncated };
}

/**
 * The slots that cannot describe an emission, because an hour they need is
 * missing or unreadable. The end hour is optional in the programme form
 * (`isOptionalEndHour`), so real programmes carry such slots, and the modal has
 * to say so rather than let them vanish from the count.
 */
export function findSlotsMissingHours(slots: TProgramSchedule[]): TProgramSchedule[] {
  return slots.filter(
    slot =>
      !isUsableHour(slot.start_hour) ||
      !isUsableHour(slot.end_hour) ||
      (!!slot.delivery_hour && !isUsableHour(slot.delivery_hour))
  );
}
